import { computed, defineComponent, h } from 'vue';
import type { DefineComponent, PropType } from 'vue';

import type { FileDiffOptions } from '../components/FileDiff';
import type {
  DiffLineAnnotation,
  SelectedLineRange,
  VirtualFileMetrics,
} from '../types';
import { getSingularPatch } from '../utils/getSingularPatch';
import { FileDiff } from './FileDiff';
import type { PatchDiffPropsVue } from './types';

// Renders a diff from a unified patch string, then delegates rendering (and all
// scoped slots) to `FileDiff`.
const PatchDiffImpl = defineComponent({
  name: 'PierrePatchDiff',
  props: {
    patch: { type: String, required: true },
    options: {
      type: Object as PropType<FileDiffOptions<undefined>>,
      default: undefined,
    },
    metrics: {
      type: Object as PropType<VirtualFileMetrics>,
      default: undefined,
    },
    lineAnnotations: {
      type: Array as PropType<DiffLineAnnotation[]>,
      default: undefined,
    },
    selectedLines: {
      type: Object as PropType<SelectedLineRange | null>,
      default: undefined,
    },
    prerenderedHTML: { type: String, default: undefined },
    disableWorkerPool: { type: Boolean, default: false },
  },
  setup(props, { slots }) {
    const fileDiff = computed(() => getSingularPatch(props.patch));
    return () =>
      h(
        FileDiff,
        {
          fileDiff: fileDiff.value,
          options: props.options,
          metrics: props.metrics,
          lineAnnotations: props.lineAnnotations,
          selectedLines: props.selectedLines,
          prerenderedHTML: props.prerenderedHTML,
          disableWorkerPool: props.disableWorkerPool,
        },
        slots
      );
  },
});

// See the note on `FileDiff` for why this explicit annotation/cast is required.
export const PatchDiff =
  PatchDiffImpl as unknown as DefineComponent<PatchDiffPropsVue>;
